import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  CreditCardIcon,
  DocumentTextIcon,
  UserIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';

export default function VoirAdhesion() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [adhesion, setAdhesion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpMessage, setHelpMessage] = useState('');
  const [sendingHelp, setSendingHelp] = useState(false);
  const [helpSuccess, setHelpSuccess] = useState(false);
  const [showPaymentDetail, setShowPaymentDetail] = useState(false);
  const [parametres, setParametres] = useState(null);
  const [hasAdhesionN1, setHasAdhesionN1] = useState(false);
  const [downloadingAttestation, setDownloadingAttestation] = useState(false);
  const [downloadingBulletin, setDownloadingBulletin] = useState(false);
  const [downloadingDocument, setDownloadingDocument] = useState({});

  useEffect(() => {
    fetchAdhesion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Récupérer les paramètres de tarifs quand l'adhésion est chargée
  useEffect(() => {
    const fetchParametres = async () => {
      if (!adhesion) return;
      
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/parametres/${adhesion.organisme}/${adhesion.annee}`
        );
        setParametres(response.data);

        // Vérifier si l'utilisateur avait une adhésion N-1 pour SAR
        if (adhesion.organisme === 'SAR') {
          const token = localStorage.getItem('token');
          const adhesionsResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/adhesions/user`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          const adhesionN1 = adhesionsResponse.data.find(
            adh => adh.organisme === 'SAR' && 
                   adh.annee === adhesion.annee - 1 && 
                   (adh.status === 'actif' || adh.status === 'expiree')
          );
          
          setHasAdhesionN1(!!adhesionN1);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des paramètres:', error);
      }
    };

    fetchParametres();
  }, [adhesion]);

  const fetchAdhesion = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/adhesions/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const adhesionData = response.data;
      
      // Bloquer l'accès si l'adhésion est avant 2026
      if (adhesionData.annee < 2026) {
        navigate('/mes-adhesions', { 
          state: { message: 'Cette adhésion n\'est plus accessible car elle date d\'avant 2026.' }
        });
        return;
      }
      
      setAdhesion(adhesionData);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement de l\'adhésion');
    } finally {
      setLoading(false);
    }
  };

  const handleSendHelp = async (e) => {
    e.preventDefault();
    setSendingHelp(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/adhesions/${id}/demande-aide`,
        { message: helpMessage },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setHelpSuccess(true);
      setTimeout(() => {
        setShowHelpModal(false);
        setHelpSuccess(false);
        setHelpMessage('');
      }, 2000);
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de l\'envoi du message');
    } finally {
      setSendingHelp(false);
    }
  };

  const handleDownloadBulletin = async () => {
    setDownloadingBulletin(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/adhesions/${id}/bulletin`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      window.open(response.data.url, '_blank');
    } catch (error) {
      console.error('Erreur téléchargement bulletin:', error);
      alert(error.response?.data?.message || 'Erreur lors du téléchargement du bulletin');
    } finally {
      setDownloadingBulletin(false);
    }
  };

  const handleDownloadDocument = async (docIndex) => {
    setDownloadingDocument(prev => ({ ...prev, [docIndex]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/adhesions/${id}/document/${docIndex}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      window.open(response.data.url, '_blank');
    } catch (error) {
      console.error('Erreur téléchargement document:', error);
      alert(error.response?.data?.message || 'Erreur lors du téléchargement du document');
    } finally {
      setDownloadingDocument(prev => ({ ...prev, [docIndex]: false }));
    }
  };

  const handleDownloadAttestation = async () => {
    setDownloadingAttestation(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/adhesions/${id}/attestation`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      window.open(response.data.url, '_blank');
    } catch (error) {
      console.error('Erreur téléchargement attestation:', error);
      alert(error.response?.data?.message || 'Erreur lors du téléchargement de l\'attestation');
    } finally {
      setDownloadingAttestation(false);
    }
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      en_attente: {
        label: 'En cours',
        color: 'bg-blue-100 text-blue-800',
        icon: ClockIcon,
        description: 'Votre adhésion est en cours de traitement'
      },
      paiement_demande: {
        label: 'En attente de paiement',
        color: 'bg-honey-100 text-honey-800',
        icon: CreditCardIcon,
        description: 'Votre adhésion est validée, en attente du paiement'
      },
      actif: {
        label: 'Actif',
        color: 'bg-green-100 text-green-800',
        icon: CheckCircleIcon,
        description: 'Votre adhésion est active'
      },
      refuse: {
        label: 'Refusé',
        color: 'bg-red-100 text-red-800',
        icon: XCircleIcon,
        description: 'Votre adhésion a été refusée'
      },
      expiree: {
        label: 'Expirée',
        color: 'bg-red-100 text-red-800',
        icon: XCircleIcon,
        description: 'Votre adhésion a expiré'
      }
    };
    return statusMap[status] || statusMap.en_cours;
  };

  const getPaymentStatusInfo = (status) => {
    const statusMap = {
      non_demande: {
        label: 'Non demandé',
        color: 'bg-slate-100 text-slate-800'
      },
      demande: {
        label: 'Paiement demandé',
        color: 'bg-blue-100 text-blue-800'
      },
      paye: {
        label: 'Payé',
        color: 'bg-green-100 text-green-800'
      },
      refuse: {
        label: 'Refusé',
        color: 'bg-red-100 text-red-800'
      }
    };
    return statusMap[status] || statusMap.en_attente;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-honey-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error || !adhesion) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-soft p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <XCircleIcon className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-slate-900 mb-2">Erreur</h3>
          <p className="text-slate-600 mb-6">{error || 'Adhésion introuvable'}</p>
          <button
            onClick={() => navigate('/mes-adhesions')}
            className="w-full bg-honey-500 text-white py-2 px-4 rounded-lg hover:bg-honey-600 transition-colors"
          >
            Retour aux adhésions
          </button>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo(adhesion.status);
  const StatusIcon = statusInfo.icon;
  const paymentStatusInfo = getPaymentStatusInfo(adhesion.paiement?.status);

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/mes-adhesions"
            className="inline-flex items-center text-honey-600 hover:text-honey-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Retour aux adhésions
          </Link>
          
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold text-slate-900">
                  Adhésion {adhesion.organisme}
                </h1>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-lg font-bold bg-honey-500 text-white shadow-sm">
                  {adhesion.annee}
                </span>
              </div>
              <p className="text-xs text-slate-600 mt-1">
                Référence : {adhesion._id}
              </p>
            </div>
            
            <button
              onClick={() => setShowHelpModal(true)}
              className="flex items-center px-4 py-2 bg-white border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <QuestionMarkCircleIcon className="h-5 w-5 mr-2" />
              Besoin d'aide
            </button>
          </div>
        </div>

        {/* Statut de l'adhésion */}
        <div className="bg-white rounded-xl shadow-soft p-6 mb-6">
          <div className="flex items-start">
            <div className={`flex-shrink-0 w-12 h-12 rounded-full ${statusInfo.color.replace('text-', 'bg-').replace('100', '200')} flex items-center justify-center`}>
              <StatusIcon className={`h-6 w-6 ${statusInfo.color.split(' ')[1]}`} />
            </div>
            <div className="ml-4 flex-1">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semilbold text-slate-900">
                  Statut de l'adhésion
                </h2>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color}`}>
                  {statusInfo.label}
                </span>
              </div>
              <p className="text-slate-600 mt-2">{statusInfo.description}</p>
              
              {/* Alerte paiement en attente */}
              {adhesion.status === 'paiement_demande' && adhesion.paiement?.status !== 'paye' && (
                <div className="mt-4 bg-honey-50 border border-honey-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <CreditCardIcon className="h-5 w-5 text-honey-600 mt-0.5 mr-3" />
                    <div className="flex-1">
                      <p className="text-sm text-honey-800 font-medium">
                        Paiement requis
                      </p>
                      <p className="text-sm text-honey-700 mt-1">
                        Votre adhésion a été validée. Veuillez procéder au paiement pour finaliser votre inscription.
                      </p>
                      <Link
                        to={`/reglement-adhesion/${adhesion._id}`}
                        className="mt-3 inline-flex items-center px-4 py-2 bg-honey-600 text-white rounded-lg hover:bg-honey-700 transition-colors text-sm font-medium"
                      >
                        Procéder au paiement
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Informations de paiement */}
        <div className="bg-white rounded-xl shadow-soft p-6 mb-6">
          <div className="flex items-center mb-4">
            <CreditCardIcon className="h-5 w-5 text-honey-600 mr-2" />
            <h2 className="text-lg font-semibold text-slate-900">
              Paiement
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <label className="text-sm font-medium text-slate-500">Montant</label>
              <div className="flex items-center gap-2">
                <p className="text-2xl font-bold text-honey-600">
                  {adhesion.paiement?.montant?.toFixed(2)} €
                </p>
                <button
                  onClick={() => setShowPaymentDetail(true)}
                  className="p-1 text-slate-400 hover:text-honey-600 hover:bg-honey-50 rounded-full transition-colors"
                  title="Voir le détail du montant"
                >
                  <QuestionMarkCircleIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">Type de paiement</label>
              <p className="text-slate-900 capitalize">
                {adhesion.paiement?.typePaiement === 'en_ligne' ? 'En ligne' : 'Chèque'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">Statut</label>
              <div>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${paymentStatusInfo.color}`}>
                  {paymentStatusInfo.label}
                </span>
              </div>
            </div>
            {adhesion.paiement?.datePaiement && (
              <div>
                <label className="text-sm font-medium text-slate-500">Date de paiement</label>
                <p className="text-slate-900">
                  {new Date(adhesion.paiement.datePaiement).toLocaleDateString('fr-FR')}
                </p>
              </div>
            )}
            {adhesion.paiement?.receiptUrl && (
              <div>
                <label className="text-sm font-medium text-slate-500">Reçu de paiement</label>
                <a
                  href={adhesion.paiement.receiptUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-honey-600 hover:text-honey-700 hover:underline"
                >
                  <DocumentTextIcon className="h-4 w-4 mr-1" />
                  Voir le reçu Stripe
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Informations personnelles */}
        <div className="bg-white rounded-xl shadow-soft p-6 mb-6">
          <div className="flex items-center mb-4">
            <UserIcon className="h-5 w-5 text-honey-600 mr-2" />
            <h2 className="text-lg font-semibold text-slate-900">
              Informations personnelles
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="text-sm font-medium text-slate-500">Type</label>
              <p className="text-slate-900">
                {(() => {
                  const type = adhesion.informationsPersonnelles?.typePersonne || 'personne_physique';
                  const labels = { personne_physique: 'Personne physique', association: 'Association', scea: 'SCEA', earl: 'EARL', etablissement_public: 'Établissement public' };
                  return labels[type] || type;
                })()}
              </p>
            </div>
            {/* {adhesion.informationsPersonnelles?.designation && (
              <div>
                <label className="text-sm font-medium text-slate-500">Civilité</label>
                <p className="text-slate-900">{adhesion.informationsPersonnelles.designation}</p>
              </div>
            )} */}
            {['association', 'scea', 'earl', 'etablissement_public'].includes(adhesion.informationsPersonnelles?.typePersonne) && adhesion.informationsPersonnelles?.raisonSociale && (
              <div className="lg:col-span-2">
                <label className="text-sm font-medium text-slate-500">Raison sociale</label>
                <p className="text-slate-900">{adhesion.informationsPersonnelles.raisonSociale}</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-slate-500">
                {['association', 'scea', 'earl', 'etablissement_public'].includes(adhesion.informationsPersonnelles?.typePersonne) ? 'Représentant légal' : 'Nom complet'}
              </label>
              <p className="text-slate-900">
                {adhesion.informationsPersonnelles?.designation} {adhesion.informationsPersonnelles?.prenom} <span className="uppercase">{adhesion.informationsPersonnelles?.nom}</span>
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">Date de naissance</label>
              <p className="text-slate-900">
                {new Date(adhesion.informationsPersonnelles?.dateNaissance).toLocaleDateString('fr-FR')}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">Email</label>
              <p className="text-slate-900 flex items-center">
                <EnvelopeIcon className="h-4 w-4 mr-2 text-slate-400" />
                {adhesion.informationsPersonnelles?.email}
              </p>
            </div>
            {adhesion.informationsPersonnelles?.telephoneMobile && (
              <div>
                <label className="text-sm font-medium text-slate-500">Téléphone portable</label>
                <p className="text-slate-900 flex items-center">
                  <PhoneIcon className="h-4 w-4 mr-2 text-slate-400" />
                  {adhesion.informationsPersonnelles.telephoneMobile || "Non renseigné"}
                </p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-slate-500">Téléphone fixe</label>
              <p className="text-slate-900 flex items-center">
                <PhoneIcon className="h-4 w-4 mr-2 text-slate-400" />
                {adhesion.informationsPersonnelles?.telephone || "Non renseigné"}
              </p>
            </div>
            <div className="col-span-2">
              <label className="text-sm font-medium text-slate-500">Adresse</label>
              <p className="text-slate-900 flex items-start">
                <MapPinIcon className="h-4 w-4 mr-2 text-slate-400 mt-0.5" />
                <span>
                  {adhesion.informationsPersonnelles?.adresse?.rue}, {adhesion.informationsPersonnelles?.adresse?.codePostal} {adhesion.informationsPersonnelles?.adresse?.ville}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Informations apicoles */}
        <div className="bg-white rounded-xl shadow-soft p-6 mb-6">
          <div className="flex items-center mb-4">
            <DocumentTextIcon className="h-5 w-5 text-honey-600 mr-2" />
            <h2 className="text-lg font-semibold text-slate-900">
              Informations apicoles
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="text-sm font-medium text-slate-500">Nombre de ruches</label>
              <p className="text-slate-900">{adhesion.nombreRuches || adhesion.informationsApicoles?.nombreRuches || 'Non renseigné'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">Nombre d'emplacements</label>
              <p className="text-slate-900">{adhesion.nombreRuchers || 'Non renseigné'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">Localisation</label>
              <p className="text-slate-900">
                {adhesion.localisation?.commune || adhesion.informationsApicoles?.localisation?.commune || 'Non renseigné'} 
                {(adhesion.localisation?.departement || adhesion.informationsApicoles?.localisation?.departement) && 
                  ` (${adhesion.localisation?.departement || adhesion.informationsApicoles?.localisation?.departement})`
                }
              </p>
            </div>
            {(adhesion.napi || adhesion.informationsApicoles?.napi) && (
              <div>
                <label className="text-sm font-medium text-slate-500">Numéro NAPI</label>
                <p className="text-slate-900">{adhesion.napi || adhesion.informationsApicoles?.napi}</p>
              </div>
            )}
            {(adhesion.numeroAmexa || adhesion.informationsApicoles?.numeroAmexa) && (
              <div>
                <label className="text-sm font-medium text-slate-500">Numéro AMEXA</label>
                <p className="text-slate-900">{adhesion.numeroAmexa || adhesion.informationsApicoles?.numeroAmexa}</p>
              </div>
            )}
            {(adhesion.siret || adhesion.informationsApicoles?.siret) && (
              <div>
                <label className="text-sm font-medium text-slate-500">SIRET</label>
                <p className="text-slate-900">{adhesion.siret || adhesion.informationsApicoles?.siret}</p>
              </div>
            )}
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">

          {/* Informations spécifiques */}
          {adhesion.organisme === 'SAR' && adhesion.informationsSpecifiques?.SAR && (
            <div className="bg-white rounded-xl shadow-soft p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">
                Informations SAR
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-500">Numéro AMEXA</label>
                  <p className="text-slate-900">{adhesion.informationsSpecifiques.SAR.numeroAmexa}</p>
                </div>
                {adhesion.informationsSpecifiques.SAR.numeroRucher && (
                  <div>
                    <label className="text-sm font-medium text-slate-500">Numéro de rucher</label>
                    <p className="text-slate-900">{adhesion.informationsSpecifiques.SAR.numeroRucher}</p>
                  </div>
                )}
                {adhesion.informationsSpecifiques.SAR.typeActivite && (
                  <div>
                    <label className="text-sm font-medium text-slate-500">Type d'activité</label>
                    <p className="text-slate-900 capitalize">{adhesion.informationsSpecifiques.SAR.typeActivite}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {adhesion.organisme === 'AMAIR' && adhesion.informationsSpecifiques?.AMAIR && (
            <div className="bg-white rounded-xl shadow-soft p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">
                Informations AMAIR
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-500">Adhérent SAR</label>
                  <p className="text-slate-900">
                    {adhesion.informationsSpecifiques.AMAIR.adherentSAR ? 'Oui' : 'Non'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Adhésion AMAIR gratuite pour adhérents SAR */}
          {adhesion.organisme === 'SAR' && adhesion.adhesionAMAIRGratuite && (
            <div className="bg-white rounded-xl shadow-soft p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">
                Adhésion à l'AMAIR
              </h2>
              <div className="bg-honey-50 border border-honey-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg className="h-6 w-6 text-honey-600 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-honey-900">
                      Adhésion gratuite à l'AMAIR incluse
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Documents */}
          {(adhesion.bulletinKey || adhesion.attestationKey || adhesion.documents?.length > 0) && (
            <div className="bg-white rounded-xl shadow-soft p-6 lg:col-span-2">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">
                Documents
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Bulletin d'adhésion */}
                {adhesion.bulletinKey && (
                  <button
                    onClick={handleDownloadBulletin}
                    disabled={downloadingBulletin}
                    className="flex items-center p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors w-full text-left disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <DocumentTextIcon className="h-5 w-5 text-honey-600 mr-3" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {downloadingBulletin ? 'Téléchargement...' : 'Bulletin d\'adhésion'}
                      </p>
                      <p className="text-xs text-slate-500">
                        Bulletin {adhesion.organisme} - Année {adhesion.annee}
                      </p>
                    </div>
                  </button>
                )}

                {/* Attestation d'adhésion */}
                {adhesion.status === 'actif' && adhesion.attestationKey && (
                  <button
                    onClick={handleDownloadAttestation}
                    disabled={downloadingAttestation}
                    className="flex items-center p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors w-full text-left disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <DocumentTextIcon className="h-5 w-5 text-honey-600 mr-3" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {downloadingAttestation ? 'Téléchargement...' : 'Attestation d\'adhésion'}
                      </p>
                      <p className="text-xs text-slate-500">
                        Attestation officielle {adhesion.organisme} - Année {adhesion.annee}
                      </p>
                    </div>
                  </button>
                )}

                {/* Documents uploadés par l'adhérent */}
                {adhesion.documents?.map((doc, index) => (
                  <button
                    key={index}
                    onClick={() => handleDownloadDocument(index)}
                    disabled={downloadingDocument[index]}
                    className="flex items-center p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors w-full text-left disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <DocumentTextIcon className="h-5 w-5 text-honey-600 mr-3" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {downloadingDocument[index] ? 'Téléchargement...' : doc.nom}
                      </p>
                      <p className="text-xs text-slate-500 capitalize">
                        {doc.type.replace('_', ' ')}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal détail du paiement */}
      {showPaymentDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-slate-900">
                Détail du montant
              </h3>
              <button
                onClick={() => setShowPaymentDetail(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* En-tête */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-slate-900">{adhesion.organisme}</p>
                    <p className="text-sm text-slate-500">Année {adhesion.annee}</p>
                  </div>
                  <p className="text-xl font-bold text-honey-600">
                    {adhesion.paiement?.montant?.toFixed(2)} €
                  </p>
                </div>
              </div>

              {/* Détail SAR */}
              {adhesion.organisme === 'SAR' && parametres?.tarifs?.SAR && (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Cotisation de base</span>
                    <span className="text-slate-900">{parametres.tarifs.SAR.base?.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Droit d'entrée</span>
                    <span className="text-slate-900">
                      {hasAdhesionN1 
                        ? <span className="text-green-600">0.00 € (exonéré - adhérent N-1)</span>
                        : `${parametres.tarifs.SAR.droitEntree?.toFixed(2)} €`
                      }
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">
                      Cotisation ruches ({adhesion.nombreRuches || 0} × {parametres.tarifs.SAR.cotisationParRuche?.toFixed(2)} €)
                    </span>
                    <span className="text-slate-900">
                      {((adhesion.nombreRuches || 0) * parametres.tarifs.SAR.cotisationParRuche).toFixed(2)} €
                    </span>
                  </div>
                  <div className="border-t border-slate-200 pt-2 mt-2 flex justify-between font-semibold">
                    <span className="text-slate-900">Total</span>
                    <span className="text-honey-600">{adhesion.paiement?.montant?.toFixed(2)} €</span>
                  </div>
                </div>
              )}

              {/* Détail AMAIR */}
              {adhesion.organisme === 'AMAIR' && parametres?.tarifs?.AMAIR && (
                <div className="space-y-2 text-sm">
                  {adhesion.informationsSpecifiques?.AMAIR?.adherentSAR ? (
                    <>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Cotisation fixe</span>
                        <span className="text-slate-400 line-through">{parametres.tarifs.AMAIR.base?.toFixed(2)} €</span>
                      </div>
                      <div className="flex justify-between text-green-600">
                        <span>Réduction adhérent SAR</span>
                        <span>- {parametres.tarifs.AMAIR.base?.toFixed(2)} €</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Cotisation fixe</span>
                      <span className="text-slate-900">{parametres.tarifs.AMAIR.base?.toFixed(2)} €</span>
                    </div>
                  )}
                  <div className="border-t border-slate-200 pt-2 mt-2 flex justify-between font-semibold">
                    <span className="text-slate-900">Total</span>
                    <span className="text-honey-600">{adhesion.paiement?.montant?.toFixed(2)} €</span>
                  </div>
                </div>
              )}

              {/* Détail ADAR */}
              {adhesion.organisme === 'ADAR' && parametres?.tarifs?.ADAR && (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Cotisation de base</span>
                    <span className="text-slate-900">{parametres.tarifs.ADAR.base?.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">
                      Cotisation ruches ({adhesion.nombreRuches || 0} × {parametres.tarifs.ADAR.cotisationParRuche?.toFixed(2)} €)
                    </span>
                    <span className="text-slate-900">
                      {((adhesion.nombreRuches || 0) * parametres.tarifs.ADAR.cotisationParRuche).toFixed(2)} €
                    </span>
                  </div>
                  <div className="border-t border-slate-200 pt-2 mt-2 flex justify-between font-semibold">
                    <span className="text-slate-900">Total</span>
                    <span className="text-honey-600">{adhesion.paiement?.montant?.toFixed(2)} €</span>
                  </div>
                </div>
              )}

              {/* Message si paramètres non disponibles */}
              {!parametres && (
                <div className="text-sm text-slate-500 italic">
                  Chargement du détail...
                </div>
              )}
            </div>

            <button
              onClick={() => setShowPaymentDetail(false)}
              className="w-full mt-6 px-4 py-2 bg-honey-500 text-white rounded-lg hover:bg-honey-600 transition-colors"
            >
              Fermer
            </button>
          </div>
        </div>
      )}

      {/* Modal d'aide */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semilbold text-slate-900">
                Besoin d'aide ?
              </h3>
              <button
                onClick={() => setShowHelpModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>

            {helpSuccess ? (
              <div className="text-center py-8">
                <CheckCircleIcon className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <p className="text-green-600 font-medium">
                  Message envoyé avec succès !
                </p>
              </div>
            ) : (
              <form onSubmit={handleSendHelp}>
                <p className="text-slate-600 mb-4">
                  Décrivez votre problème ou votre question concernant cette adhésion. 
                  Un administrateur du groupement {adhesion.organisme} vous répondra par email.
                </p>
                
                <textarea
                  value={helpMessage}
                  onChange={(e) => setHelpMessage(e.target.value)}
                  required
                  rows={5}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-honey-500 focus:border-honey-500"
                  placeholder="Décrivez votre demande..."
                />

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowHelpModal(false)}
                    className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={sendingHelp}
                    className="flex-1 px-4 py-2 bg-honey-500 text-white rounded-lg hover:bg-honey-600 transition-colors disabled:bg-slate-400"
                  >
                    {sendingHelp ? 'Envoi...' : 'Envoyer'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
